import {
  BROKERAGE_READ_PERMISSION,
  CLIENT_MANAGEMENT_PERMISSION,
  DEV_USE_PERMISSION,
  FILES_MANAGE_PERMISSION,
  LEAD_READ_PERMISSION,
  LOOKER_PERMISSION,
  PARTNER_CENTER_PERMISSION,
  SELLER_DASHBOARD_READ_PERMISSION,
  SUPPORT_TICKETS_PERMISSION,
  TEMPLATE_SENDER_READ_PERMISSION,
  TEMPLATES_PERMISSION,
  COUNTRY_ADMIN_PERMISSION,
  ADMIN_PERMISSION,
  USER_ANALYSIS_READ_PERMISSIONS,
  FORBIDDEN_ANALYSIS_PERMISSIONS,
  WHATSAPP_FLOWS_READ_PERMISSION,
  WHATSAPP_FLOWS_MANAGE_PERMISSION,
} from '../utils/constPermissions';
import { DEFAULT_COUNTRY_CODE } from '../utils/countries';
import { MATCH_MODE_ALL, MATCH_MODE_ANY, type RoutePermissionSet } from '../utils/permissions';

/**
 * Centralized route permissions configuration.
 * Maps route paths to their required permissions (allow/deny) and matchMode.
 * This ensures consistency between route protection and sidebar filtering.
 */
export const routePermissions: Record<string, RoutePermissionSet> = {
  '/clients': { allow: CLIENT_MANAGEMENT_PERMISSION, matchMode: MATCH_MODE_ANY },
  '/templates': { allow: [TEMPLATES_PERMISSION], matchMode: MATCH_MODE_ALL },
  '/template-sender': { allow: [TEMPLATE_SENDER_READ_PERMISSION], matchMode: MATCH_MODE_ALL },
  '/whatsapp-flows': {
    allow: [WHATSAPP_FLOWS_READ_PERMISSION, WHATSAPP_FLOWS_MANAGE_PERMISSION],
    matchMode: MATCH_MODE_ANY,
  },
  '/funnel': {
    allow: CLIENT_MANAGEMENT_PERMISSION,
    matchMode: MATCH_MODE_ANY,
  },
  '/leads': {
    allow: [LEAD_READ_PERMISSION],
    matchMode: MATCH_MODE_ALL,
  },
  '/seller-dashboard': {
    allow: [SELLER_DASHBOARD_READ_PERMISSION],
    matchMode: MATCH_MODE_ALL,
  },
  '/partner-center': {
    allow: [PARTNER_CENTER_PERMISSION, SUPPORT_TICKETS_PERMISSION, FILES_MANAGE_PERMISSION],
    matchMode: MATCH_MODE_ANY,
  },
  '/looker': {
    allow: [LOOKER_PERMISSION],
    matchMode: MATCH_MODE_ALL,
  },
  '/db-reader': {
    allow: [ADMIN_PERMISSION],
    deny: [COUNTRY_ADMIN_PERMISSION],
    matchMode: MATCH_MODE_ALL,
    includedCountryCodes: [DEFAULT_COUNTRY_CODE],
  },
  '/mortgage-analysis': {
    allow: [ADMIN_PERMISSION],
    deny: [COUNTRY_ADMIN_PERMISSION],
    matchMode: MATCH_MODE_ALL,
  },
  '/rbac': {
    allow: [DEV_USE_PERMISSION],
    deny: [COUNTRY_ADMIN_PERMISSION],
    matchMode: MATCH_MODE_ALL,
  },
  '/brokerage': {
    allow: [BROKERAGE_READ_PERMISSION],
    deny: [COUNTRY_ADMIN_PERMISSION],
    matchMode: MATCH_MODE_ALL,
  },
  '/calls': {
    allow: ['clients.read'],
    matchMode: MATCH_MODE_ALL,
  },
  '/analysis': {
    allow: USER_ANALYSIS_READ_PERMISSIONS,
    deny: FORBIDDEN_ANALYSIS_PERMISSIONS,
    matchMode: MATCH_MODE_ANY,
  },
};

export type RoutePermissionPath = keyof typeof routePermissions;

/**
 * Get permissions for a given route path.
 * @param path - The route path to get permissions for
 * @returns RoutePermissionSet or undefined if no permissions are required
 */
export const getRoutePermissions = (path: RoutePermissionPath): RoutePermissionSet | undefined => {
  return routePermissions[path];
};

export const LEGACY_CLIENTS_VIEW_ACCESS_STATE = { legacyClientsViewAccess: true } as const;

export const hasLegacyClientsViewAccess = (state: unknown): boolean =>
  typeof state === 'object' &&
  state !== null &&
  'legacyClientsViewAccess' in state &&
  state.legacyClientsViewAccess === true;
